import type {
  Accent,
  DestructibleDensity,
  Difficulty,
  FairnessMetadata,
  HazardKind,
  MapManifest,
  ObjectiveKind,
  SpawnManifest,
  SymmetryKind,
} from "./content-types";

const OPEN = "...........";

// These motifs keep an odd-column floor spine open while authored blocks vary
// the sightlines and blast timing from stage to stage.
const ODD_A = ".+...+...+.";
const ODD_B = "...+...+...";
const ODD_C = ".+.+...+.+.";
const ODD_D = ".....+.....";
const ODD_E = ".+.....+...";
const ODD_F = "...+.+...+.";
const ODD_G = ".+...+.+...";
const ODD_H = "...+...+.+.";
const ODD_I = ".+.+.+...+.";
const ODD_J = "...+.+.+...";
const ODD_K = ".+.+...+...";

const EVEN_A = ".#.#.#.#.#.";
const EVEN_B = ".+.#.#.+.#.";
const EVEN_C = ".#.+.+.#.#.";
const EVEN_D = ".+.#.+.#.+.";
const EVEN_E = ".#.+.#.+.#.";
const EVEN_F = ".+.+.#.#.+.";
const EVEN_G = ".#.#.+.+.#.";
const EVEN_H = ".+.#.#.#.+.";
const EVEN_I = ".#.+.#.#.+.";
const EVEN_J = ".+.+.#.+.#.";

function campaignRows(
  y2: string,
  y3: string,
  y4: string,
  y5: string,
  y6: string,
  y7: string,
  y8: string,
): readonly string[] {
  return Object.freeze([
    "#############",
    `#${OPEN}#`,
    `#${y2}#`,
    `#${y3}#`,
    `#${y4}#`,
    `#${y5}#`,
    `#${y6}#`,
    `#${y7}#`,
    `#${y8}#`,
    `#${OPEN}#`,
    "#############",
  ]);
}

const CAMPAIGN_SPAWNS: SpawnManifest = Object.freeze({
  p1: Object.freeze({ x: 1, y: 1 }),
  p2: Object.freeze({ x: 11, y: 9 }),
  p3: Object.freeze({ x: 11, y: 1 }),
  p4: Object.freeze({ x: 1, y: 9 }),
});

const SPAWN_SAFETY = Object.freeze({
  verified: true,
  clearRadius: 1,
  minimumEscapeRoutes: 2,
  notes: "Corner cells retain two floor exits and a clear one-cell buffer.",
});

interface StageSpec {
  readonly name: string;
  readonly tagline: string;
  readonly rows: readonly string[];
  readonly hazard: HazardKind;
  readonly objective: ObjectiveKind;
  readonly difficulty: Difficulty;
  readonly symmetry: SymmetryKind;
  readonly destructibleDensity: DestructibleDensity;
}

function defineCircuitMaps(
  circuitId: string,
  accent: Accent,
  stages: readonly StageSpec[],
): readonly MapManifest[] {
  return Object.freeze(
    stages.map((stage, index) =>
      Object.freeze({
        id: `${circuitId}-${String(index + 1).padStart(2, "0")}`,
        name: stage.name,
        circuitId,
        stage: index + 1,
        tagline: stage.tagline,
        accent,
        rows: stage.rows,
        spawns: CAMPAIGN_SPAWNS,
        hazard: stage.hazard,
        objective: stage.objective,
        difficulty: stage.difficulty,
        boss: index === stages.length - 1,
        fairness: Object.freeze({
          symmetry: stage.symmetry,
          destructibleDensity: stage.destructibleDensity,
          spawnSafety: SPAWN_SAFETY,
        } satisfies FairnessMetadata),
      } satisfies MapManifest),
    ),
  );
}

const PRISMWAKE_MAPS = defineCircuitMaps("prismwake", "cyan", [
  {
    name: "Glassroot",
    tagline: "Find the clean line before the first flare.",
    rows: campaignRows(EVEN_A, ODD_A, EVEN_B, ODD_B, EVEN_C, ODD_C, EVEN_D),
    hazard: "none",
    objective: "breakthrough",
    difficulty: 1,
    symmetry: "rotational",
    destructibleDensity: "light",
  },
  {
    name: "Signal Orchard",
    tagline: "Every bright branch hides a shorter route.",
    rows: campaignRows(EVEN_B, ODD_B, EVEN_C, ODD_C, EVEN_D, ODD_D, EVEN_E),
    hazard: "none",
    objective: "relay",
    difficulty: 1,
    symmetry: "mirrored",
    destructibleDensity: "light",
  },
  {
    name: "Amber Switchyard",
    tagline: "Trade open space for a decisive crossing.",
    rows: campaignRows(EVEN_C, ODD_C, EVEN_D, ODD_D, EVEN_E, ODD_E, EVEN_F),
    hazard: "pulse-lanes",
    objective: "control",
    difficulty: 2,
    symmetry: "lane-balanced",
    destructibleDensity: "medium",
  },
  {
    name: "Prism Bend",
    tagline: "The safest corner moves with the light.",
    rows: campaignRows(EVEN_D, ODD_D, EVEN_E, ODD_E, EVEN_F, ODD_F, EVEN_G),
    hazard: "pulse-lanes",
    objective: "breakthrough",
    difficulty: 2,
    symmetry: "rotational",
    destructibleDensity: "medium",
  },
  {
    name: "Lumen Hollows",
    tagline: "Quiet lanes make loud mistakes.",
    rows: campaignRows(EVEN_E, ODD_E, EVEN_F, ODD_F, EVEN_G, ODD_G, EVEN_H),
    hazard: "ember-drift",
    objective: "survive",
    difficulty: 3,
    symmetry: "mirrored",
    destructibleDensity: "medium",
  },
  {
    name: "Split Current",
    tagline: "Two routes open; only one stays open.",
    rows: campaignRows(EVEN_F, ODD_F, EVEN_G, ODD_G, EVEN_H, ODD_H, EVEN_I),
    hazard: "ember-drift",
    objective: "relay",
    difficulty: 3,
    symmetry: "lane-balanced",
    destructibleDensity: "heavy",
  },
  {
    name: "Beacon Well",
    tagline: "Hold the middle without losing the exits.",
    rows: campaignRows(EVEN_G, ODD_G, EVEN_H, ODD_H, EVEN_I, ODD_I, EVEN_J),
    hazard: "closing-ring",
    objective: "control",
    difficulty: 4,
    symmetry: "rotational",
    destructibleDensity: "heavy",
  },
  {
    name: "Prismwake Core",
    tagline: "The circuit turns its final signal against you.",
    rows: campaignRows(EVEN_H, ODD_H, EVEN_I, ODD_I, EVEN_J, ODD_J, EVEN_A),
    hazard: "closing-ring",
    objective: "warden",
    difficulty: 4,
    symmetry: "mirrored",
    destructibleDensity: "heavy",
  },
]);

const COPPER_CANOPY_MAPS = defineCircuitMaps("copper-canopy", "amber", [
  {
    name: "Canopy Loop",
    tagline: "Start under cover, finish in the open.",
    rows: campaignRows(EVEN_B, ODD_C, EVEN_D, ODD_A, EVEN_F, ODD_D, EVEN_H),
    hazard: "none",
    objective: "breakthrough",
    difficulty: 2,
    symmetry: "mirrored",
    destructibleDensity: "light",
  },
  {
    name: "Rivet Garden",
    tagline: "A careful rhythm beats a crowded route.",
    rows: campaignRows(EVEN_C, ODD_D, EVEN_E, ODD_B, EVEN_G, ODD_E, EVEN_I),
    hazard: "pulse-lanes",
    objective: "relay",
    difficulty: 2,
    symmetry: "lane-balanced",
    destructibleDensity: "medium",
  },
  {
    name: "Copper Vines",
    tagline: "Let the side lanes do the heavy lifting.",
    rows: campaignRows(EVEN_D, ODD_E, EVEN_F, ODD_C, EVEN_H, ODD_F, EVEN_J),
    hazard: "pulse-lanes",
    objective: "control",
    difficulty: 2,
    symmetry: "rotational",
    destructibleDensity: "medium",
  },
  {
    name: "Bolt Market",
    tagline: "Open a stall, then defend the whole row.",
    rows: campaignRows(EVEN_E, ODD_F, EVEN_G, ODD_D, EVEN_I, ODD_G, EVEN_A),
    hazard: "ember-drift",
    objective: "breakthrough",
    difficulty: 3,
    symmetry: "mirrored",
    destructibleDensity: "medium",
  },
  {
    name: "Overstory Relay",
    tagline: "The long way around is sometimes the fast way out.",
    rows: campaignRows(EVEN_F, ODD_G, EVEN_H, ODD_E, EVEN_J, ODD_H, EVEN_B),
    hazard: "ember-drift",
    objective: "relay",
    difficulty: 3,
    symmetry: "lane-balanced",
    destructibleDensity: "heavy",
  },
  {
    name: "Rainline Works",
    tagline: "Count the gaps between falling sparks.",
    rows: campaignRows(EVEN_G, ODD_H, EVEN_I, ODD_F, EVEN_A, ODD_I, EVEN_C),
    hazard: "tide-rise",
    objective: "survive",
    difficulty: 4,
    symmetry: "rotational",
    destructibleDensity: "heavy",
  },
  {
    name: "Canopy Fork",
    tagline: "A split route becomes a shared problem.",
    rows: campaignRows(EVEN_H, ODD_I, EVEN_J, ODD_G, EVEN_B, ODD_J, EVEN_D),
    hazard: "tide-rise",
    objective: "control",
    difficulty: 4,
    symmetry: "mirrored",
    destructibleDensity: "heavy",
  },
  {
    name: "Copper Crown",
    tagline: "The canopy closes over the last clean lane.",
    rows: campaignRows(EVEN_I, ODD_J, EVEN_A, ODD_H, EVEN_C, ODD_K, EVEN_E),
    hazard: "closing-ring",
    objective: "warden",
    difficulty: 5,
    symmetry: "lane-balanced",
    destructibleDensity: "heavy",
  },
]);

const MOONLIT_FOUNDRY_MAPS = defineCircuitMaps("moonlit-foundry", "violet", [
  {
    name: "Night Shift",
    tagline: "The first bell leaves plenty of room to learn.",
    rows: campaignRows(EVEN_J, ODD_A, EVEN_I, ODD_D, EVEN_H, ODD_G, EVEN_G),
    hazard: "none",
    objective: "breakthrough",
    difficulty: 2,
    symmetry: "rotational",
    destructibleDensity: "light",
  },
  {
    name: "Meltwater Bay",
    tagline: "Cool channels, hot corners, no wasted step.",
    rows: campaignRows(EVEN_A, ODD_B, EVEN_J, ODD_E, EVEN_I, ODD_H, EVEN_F),
    hazard: "pulse-lanes",
    objective: "relay",
    difficulty: 2,
    symmetry: "mirrored",
    destructibleDensity: "medium",
  },
  {
    name: "Cinder Press",
    tagline: "The press never stops, so neither can you.",
    rows: campaignRows(EVEN_B, ODD_C, EVEN_A, ODD_F, EVEN_J, ODD_I, EVEN_H),
    hazard: "ember-drift",
    objective: "survive",
    difficulty: 3,
    symmetry: "lane-balanced",
    destructibleDensity: "medium",
  },
  {
    name: "Quiet Crucible",
    tagline: "Silence gives the center too much weight.",
    rows: campaignRows(EVEN_C, ODD_D, EVEN_B, ODD_G, EVEN_A, ODD_J, EVEN_I),
    hazard: "ember-drift",
    objective: "control",
    difficulty: 3,
    symmetry: "rotational",
    destructibleDensity: "medium",
  },
  {
    name: "Foundry Steps",
    tagline: "Take the stairway before it becomes a wall.",
    rows: campaignRows(EVEN_D, ODD_E, EVEN_C, ODD_H, EVEN_B, ODD_A, EVEN_J),
    hazard: "tide-rise",
    objective: "breakthrough",
    difficulty: 4,
    symmetry: "mirrored",
    destructibleDensity: "heavy",
  },
  {
    name: "Blue Furnace",
    tagline: "Cold light makes every fuse look closer.",
    rows: campaignRows(EVEN_E, ODD_F, EVEN_D, ODD_I, EVEN_C, ODD_B, EVEN_A),
    hazard: "tide-rise",
    objective: "relay",
    difficulty: 4,
    symmetry: "lane-balanced",
    destructibleDensity: "heavy",
  },
  {
    name: "Moon Kiln",
    tagline: "The kiln rewards patience, then tests it.",
    rows: campaignRows(EVEN_F, ODD_G, EVEN_E, ODD_J, EVEN_D, ODD_C, EVEN_B),
    hazard: "closing-ring",
    objective: "survive",
    difficulty: 4,
    symmetry: "rotational",
    destructibleDensity: "heavy",
  },
  {
    name: "Foundry Heart",
    tagline: "A small chamber with a very large answer.",
    rows: campaignRows(EVEN_G, ODD_H, EVEN_F, ODD_K, EVEN_E, ODD_D, EVEN_C),
    hazard: "closing-ring",
    objective: "warden",
    difficulty: 5,
    symmetry: "mirrored",
    destructibleDensity: "heavy",
  },
]);

const TIDAL_RELAY_MAPS = defineCircuitMaps("tidal-relay", "coral", [
  {
    name: "Lowwater Gate",
    tagline: "The tide has not chosen a side yet.",
    rows: campaignRows(EVEN_H, ODD_K, EVEN_G, ODD_A, EVEN_F, ODD_C, EVEN_E),
    hazard: "none",
    objective: "breakthrough",
    difficulty: 2,
    symmetry: "lane-balanced",
    destructibleDensity: "light",
  },
  {
    name: "Foamline",
    tagline: "Keep one dry route in reserve.",
    rows: campaignRows(EVEN_I, ODD_A, EVEN_H, ODD_B, EVEN_G, ODD_D, EVEN_F),
    hazard: "pulse-lanes",
    objective: "relay",
    difficulty: 3,
    symmetry: "rotational",
    destructibleDensity: "medium",
  },
  {
    name: "Breakwater Nine",
    tagline: "The walls are sturdy; the openings are not.",
    rows: campaignRows(EVEN_J, ODD_B, EVEN_I, ODD_C, EVEN_H, ODD_E, EVEN_G),
    hazard: "pulse-lanes",
    objective: "control",
    difficulty: 3,
    symmetry: "mirrored",
    destructibleDensity: "medium",
  },
  {
    name: "Drift Channel",
    tagline: "Let momentum carry you past the danger.",
    rows: campaignRows(EVEN_A, ODD_C, EVEN_J, ODD_D, EVEN_I, ODD_F, EVEN_H),
    hazard: "tide-rise",
    objective: "survive",
    difficulty: 3,
    symmetry: "lane-balanced",
    destructibleDensity: "medium",
  },
  {
    name: "Red Buoy Yard",
    tagline: "The marker is safe only until it moves.",
    rows: campaignRows(EVEN_B, ODD_D, EVEN_A, ODD_E, EVEN_J, ODD_G, EVEN_I),
    hazard: "tide-rise",
    objective: "relay",
    difficulty: 4,
    symmetry: "rotational",
    destructibleDensity: "heavy",
  },
  {
    name: "Undertow Court",
    tagline: "The center pulls harder than it looks.",
    rows: campaignRows(EVEN_C, ODD_E, EVEN_B, ODD_F, EVEN_A, ODD_H, EVEN_J),
    hazard: "ember-drift",
    objective: "control",
    difficulty: 4,
    symmetry: "mirrored",
    destructibleDensity: "heavy",
  },
  {
    name: "Relay Shoals",
    tagline: "Pass the pressure before the water turns.",
    rows: campaignRows(EVEN_D, ODD_F, EVEN_C, ODD_G, EVEN_B, ODD_I, EVEN_A),
    hazard: "closing-ring",
    objective: "breakthrough",
    difficulty: 5,
    symmetry: "lane-balanced",
    destructibleDensity: "heavy",
  },
  {
    name: "Tidal Crown",
    tagline: "The final relay runs through a living current.",
    rows: campaignRows(EVEN_E, ODD_G, EVEN_D, ODD_H, EVEN_C, ODD_J, EVEN_B),
    hazard: "closing-ring",
    objective: "warden",
    difficulty: 5,
    symmetry: "rotational",
    destructibleDensity: "heavy",
  },
]);

const SKYLINE_STATIC_MAPS = defineCircuitMaps("skyline-static", "lime", [
  {
    name: "Roofline",
    tagline: "The city lights make a generous first outline.",
    rows: campaignRows(EVEN_F, ODD_H, EVEN_E, ODD_I, EVEN_D, ODD_K, EVEN_C),
    hazard: "none",
    objective: "breakthrough",
    difficulty: 3,
    symmetry: "mirrored",
    destructibleDensity: "light",
  },
  {
    name: "Static Garden",
    tagline: "Stay grounded while the lanes start to sing.",
    rows: campaignRows(EVEN_G, ODD_I, EVEN_F, ODD_J, EVEN_E, ODD_A, EVEN_D),
    hazard: "pulse-lanes",
    objective: "relay",
    difficulty: 3,
    symmetry: "lane-balanced",
    destructibleDensity: "medium",
  },
  {
    name: "Aerial Switch",
    tagline: "The high route is clear until it is not.",
    rows: campaignRows(EVEN_H, ODD_J, EVEN_G, ODD_K, EVEN_F, ODD_B, EVEN_E),
    hazard: "skyline-crossfire",
    objective: "control",
    difficulty: 4,
    symmetry: "rotational",
    destructibleDensity: "medium",
  },
  {
    name: "Violet Overpass",
    tagline: "Cross once, then make the crossing count.",
    rows: campaignRows(EVEN_I, ODD_K, EVEN_H, ODD_A, EVEN_G, ODD_C, EVEN_F),
    hazard: "skyline-crossfire",
    objective: "survive",
    difficulty: 4,
    symmetry: "mirrored",
    destructibleDensity: "heavy",
  },
  {
    name: "Cloudline Exchange",
    tagline: "A wide view does not mean a safe one.",
    rows: campaignRows(EVEN_J, ODD_A, EVEN_I, ODD_B, EVEN_H, ODD_D, EVEN_G),
    hazard: "skyline-crossfire",
    objective: "relay",
    difficulty: 4,
    symmetry: "lane-balanced",
    destructibleDensity: "heavy",
  },
  {
    name: "Signal Verge",
    tagline: "Read the rhythm between two distant flashes.",
    rows: campaignRows(EVEN_A, ODD_B, EVEN_J, ODD_C, EVEN_I, ODD_E, EVEN_H),
    hazard: "closing-ring",
    objective: "control",
    difficulty: 5,
    symmetry: "rotational",
    destructibleDensity: "heavy",
  },
  {
    name: "Last Antenna",
    tagline: "The signal is loudest where the exits narrow.",
    rows: campaignRows(EVEN_B, ODD_C, EVEN_A, ODD_D, EVEN_J, ODD_F, EVEN_I),
    hazard: "closing-ring",
    objective: "survive",
    difficulty: 5,
    symmetry: "mirrored",
    destructibleDensity: "heavy",
  },
  {
    name: "Skyline Static",
    tagline: "Bring the whole skyline back into phase.",
    rows: campaignRows(EVEN_C, ODD_D, EVEN_B, ODD_E, EVEN_A, ODD_G, EVEN_J),
    hazard: "skyline-crossfire",
    objective: "warden",
    difficulty: 5,
    symmetry: "lane-balanced",
    destructibleDensity: "heavy",
  },
]);

export const CAMPAIGN_MAPS: readonly MapManifest[] = Object.freeze([
  ...PRISMWAKE_MAPS,
  ...COPPER_CANOPY_MAPS,
  ...MOONLIT_FOUNDRY_MAPS,
  ...TIDAL_RELAY_MAPS,
  ...SKYLINE_STATIC_MAPS,
]);

export { CAMPAIGN_SPAWNS };
